import { useEffect, useMemo, useRef } from 'react';
import useSWRInfinite, { SWRInfiniteKeyLoader } from 'swr/infinite';
import { GenericV1ErrorsArray } from 'src/types/v1-api-types/_global';
import { fetcherSWR } from 'src/utils/data-fetching/fetcher';
import { buildSearchParams } from '@utils/data-fetching/searchParams';
import { McUnifiedProfileQueryParams, McUnifiedProfileResult } from '@components/templates/employer/applicant-list/flow-mc/types';
import { SwrApiKey } from '@type/swr';

interface UseSwrMcUnifiedProfileListProps {
  locale?: string;
  jobSlug: string;
  serverSideData?: McUnifiedProfileResult;
  startIndex?: number;
  perPage?: number;
  queryParams?: Omit<McUnifiedProfileQueryParams, 'page' | 'perPage'>;
  shouldFetch?: boolean;
}

export const useSwrMcUnifiedProfileList = (props: UseSwrMcUnifiedProfileListProps) => {
  const {
    locale = 'ro',
    jobSlug,
    serverSideData,
    startIndex = 1,
    perPage = 24,
    queryParams,
    shouldFetch = true,
  } = props;

  const sanitizedQueryParams = useMemo(
    () => queryParams ?? {},
    [queryParams],
  );
  const urlParams = useMemo(
    () => buildSearchParams(sanitizedQueryParams, '&', false),
    [sanitizedQueryParams],
  );

  const initialKeyRef = useRef<string | null>(null);
  const currentKey = `${jobSlug}|${perPage}|${startIndex}|${urlParams}`;
  if (initialKeyRef.current === null) {
    initialKeyRef.current = currentKey;
  }

  const fallbackData = useMemo(
    () => (initialKeyRef.current === currentKey && serverSideData ? [serverSideData] : undefined),
    [currentKey, serverSideData],
  );

  const getKey: SWRInfiniteKeyLoader<McUnifiedProfileResult, SwrApiKey | null> = (pageIndex, previousPageData) => {
    if (!shouldFetch || !jobSlug) return null;

    const currentPage = previousPageData?.page ?? 0;
    const totalPages = previousPageData?.pages ?? 1;

    if (previousPageData && currentPage >= totalPages) {
      return null;
    }

    const page = pageIndex + startIndex;
    return [
      `/v1/profile/${jobSlug}/list?page=${page}&perPage=${perPage}${urlParams}`,
      locale,
      {},
    ];
  };

  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
  } = useSWRInfinite<McUnifiedProfileResult, GenericV1ErrorsArray, SWRInfiniteKeyLoader<McUnifiedProfileResult, SwrApiKey | null>>(
    getKey,
    fetcherSWR,
    {
      fallbackData,
      revalidateOnMount: !fallbackData,
      keepPreviousData: true,
      revalidateOnFocus: false,
      parallel: true,
    },
  );

  const sizeAsRef = useRef(size);
  useEffect(() => {
    sizeAsRef.current = size;
  }, [size]);

  const lastPage = data?.at(-1);

  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    isEmpty: Boolean(lastPage && (lastPage.items?.length ?? 0) === 0 && (lastPage.total ?? 0) === 0),
    showLoadMore: Boolean(lastPage && (lastPage.page ?? 1) < (lastPage.pages ?? 1)),
    sizeAsRef,
  };
};
